import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { customerAPI, supplierAPI, productAPI } from '../utils/api';
import { useAuth } from './AuthContext';

const DataContext = createContext(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within DataProvider');
  return context;
};

export const DataProvider = ({ children }) => {
  const { isAuthenticated, userType, loading: authLoading } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);

  // Track last fetch time to avoid redundant refetches
  const lastFetch = useRef({ customers: 0, suppliers: 0, products: 0 });
  const STALE_MS = 30000; // 30s staleness window

  const refreshCustomers = useCallback(async (force = false) => {
    if (!force && Date.now() - lastFetch.current.customers < STALE_MS) return;
    setCustomersLoading(true);
    try {
      const res = await customerAPI.list({ page_size: 10000 });
      const list = res.data?.customers || res.data || [];
      setCustomers(list);
      lastFetch.current.customers = Date.now();
    } catch (e) { console.error('Failed to load customers:', e); }
    setCustomersLoading(false);
  }, []);

  const refreshSuppliers = useCallback(async (force = false) => {
    if (!force && Date.now() - lastFetch.current.suppliers < STALE_MS) return;
    setSuppliersLoading(true);
    try {
      const res = await supplierAPI.list({ page_size: 10000 });
      const list = res.data?.suppliers || res.data || [];
      setSuppliers(list);
      lastFetch.current.suppliers = Date.now();
    } catch (e) { console.error('Failed to load suppliers:', e); }
    setSuppliersLoading(false);
  }, []);

  const refreshProducts = useCallback(async (force = false) => {
    if (!force && Date.now() - lastFetch.current.products < STALE_MS) return;
    setProductsLoading(true);
    try {
      const res = await productAPI.list({ page_size: 10000 });
      const list = res.data?.products || res.data || [];
      setProducts(list);
      lastFetch.current.products = Date.now();
    } catch (e) { console.error('Failed to load products:', e); }
    setProductsLoading(false);
  }, []);

  const refreshAll = useCallback(async (force = false) => {
    await Promise.all([
      refreshCustomers(force),
      refreshSuppliers(force),
      refreshProducts(force),
    ]);
  }, [refreshCustomers, refreshSuppliers, refreshProducts]);

  // Auto-load on authentication (skip for admin users who don't have shop data)
  useEffect(() => {
    if (authLoading) return; // Wait for auth to initialize
    if (isAuthenticated && userType !== 'admin') {
      refreshAll(true);
    }
    if (!isAuthenticated) {
      setCustomers([]);
      setSuppliers([]);
      setProducts([]);
      lastFetch.current = { customers: 0, suppliers: 0, products: 0 };
    }
  }, [authLoading, isAuthenticated, userType, refreshAll]);

  const value = {
    customers,
    suppliers,
    products,
    customersLoading,
    suppliersLoading,
    productsLoading,
    refreshCustomers,
    refreshSuppliers,
    refreshProducts,
    refreshAll,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
